import React, { useCallback, useEffect, useState } from 'react';
import { tenantsService } from '../services/tenants.service';
import { TenantDetail, TenantDocument, TENANT_DOCUMENT_TYPE_LABELS } from '../types/tenant';
import { formatCurrency, formatDate } from '../utils/format';
import { getErrorMessage } from '../utils/error';
import MentionBail from '../components/MentionBail';
import '../styles/TenantDossierPage.css';

/**
 * « Mon dossier » — le locataire consulte sa propre fiche : chambre, loyer,
 * provision pour charges, dates (entrée, préavis, sortie prévue) et la totalité
 * de ses documents, téléchargeables.
 */
interface TenantDossierPageProps {
  /** Aperçu du propriétaire : le dossier est fourni, tel que le locataire le reçoit. */
  apercu?: TenantDetail;
}

const TenantDossierPage: React.FC<TenantDossierPageProps> = ({ apercu }) => {
  const [dossier, setDossier] = useState<TenantDetail | null>(apercu ?? null);
  const [loading, setLoading] = useState(!apercu);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDossier(await tenantsService.getMyDossier());
    } catch (err) {
      setError(getErrorMessage(err, 'Impossible de charger votre dossier'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!apercu) load();
  }, [load, apercu]);

  const handleDownload = async (doc: TenantDocument) => {
    if (!dossier) return;
    try {
      const blob = await tenantsService.downloadDocument(dossier.id, doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du téléchargement'));
    }
  };

  if (loading) {
    return <div className="dossier-loading">Chargement...</div>;
  }

  if (error && !dossier) {
    return <div className="dossier-error">{error}</div>;
  }

  if (!dossier) {
    return <div className="dossier-empty">Aucun dossier locataire.</div>;
  }

  return (
    <div className="dossier-page">
      <h2 className="dossier-title">Mon dossier</h2>

      {error && <div className="dossier-error">{error}</div>}

      <div className="dossier-grid">
        <section className="dossier-section">
          <h3>Ma location</h3>
          <div className="dossier-item"><span>Chambre</span><span>{dossier.roomName || '-'}</span></div>
          <div className="dossier-item"><span>Loyer mensuel</span><span>{dossier.monthlyRent != null ? formatCurrency(dossier.monthlyRent) : '-'}</span></div>
          <div className="dossier-item"><span>Provision pour charges</span><span>{dossier.chargesProvision != null ? formatCurrency(dossier.chargesProvision) : '-'}</span></div>
        </section>

        <section className="dossier-section">
          <h3>Mes dates</h3>
          <div className="dossier-item"><span>Début du bail</span><span>{dossier.leaseStartDate ? formatDate(dossier.leaseStartDate) : '-'}</span></div>
          {/* Les états des lieux ne sont pas décoratifs pour le locataire :
              celui de sortie fixe le jour où il doit avoir rendu les clés, et
              détermine la fermeture de son accès à cet espace six mois plus
              tard. Ne montrer que le début et la fin de bail lui cachait la
              seule date qui l'engage concrètement. */}
          <div className="dossier-item"><span>État des lieux d&apos;entrée</span><span>{dossier.entryInventoryDate ? formatDate(dossier.entryInventoryDate) : '-'}</span></div>
          <div className="dossier-item"><span>Préavis déposé le</span><span>{dossier.noticeDate ? formatDate(dossier.noticeDate) : '-'}</span></div>
          <div className="dossier-item"><span>Sortie prévue</span><span>{dossier.noticeExpectedExitDate ? formatDate(dossier.noticeExpectedExitDate) : '-'}</span></div>
          <div className="dossier-item"><span>État des lieux de sortie</span><span>{dossier.exitInventoryDate ? formatDate(dossier.exitInventoryDate) : '-'}</span></div>
          <div className="dossier-item"><span>Fin de bail</span><span>{dossier.leaseEndDate ? formatDate(dossier.leaseEndDate) : '-'}</span></div>
        </section>
      </div>

      <section className="dossier-section dossier-docs">
        <h3>Mes documents</h3>
        {dossier.documents.length === 0 ? (
          <p className="dossier-no-docs">Aucun document</p>
        ) : (
          <div className="dossier-docs-list">
            {dossier.documents.map(doc => (
              <div key={doc.id} className="dossier-doc-item">
                <span className="dossier-doc-badge">
                  {TENANT_DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                </span>
                <button
                  type="button"
                  className="dossier-doc-name"
                  onClick={() => handleDownload(doc)}
                  title="Télécharger"
                >
                  {doc.fileName}
                </button>
                <span className="dossier-doc-date">{formatDate(doc.uploadedAt)}</span>
              </div>
            ))}
          </div>
        )}
        {dossier.documents.some(d => d.documentType === 'LEASE') && <MentionBail />}
      </section>
    </div>
  );
};

export default TenantDossierPage;
